import Vue from 'vue'
import { GetterTree, MutationTree, ActionTree } from 'vuex'
import { arrayUnion, arrayRemove } from 'vuex-easy-firestore'
import _find from 'lodash/find'
import _forEach from 'lodash/forEach'
import _sample from 'lodash/sample'

import i18n from '@/i18n'
import { authService, sendSignInLink } from '@/service/firebase'

import starNames from '@/data/starnames'
import { IUser } from '@/models'

const defaultUser: IUser = {
    id: '0',
    name: 'Anonymous',
    hasAccount: false,
    isRegistered: false,
    preferredLanguage: i18n.locale,
    relatedAssociations: [],
    relatedComments: [],
    relatedVotes: {}
}

class State {
    _users: { [key: string]: IUser } = {}
    _currentUser: IUser = defaultUser
}

const getters = {
    users( state ) {
        return state._users
    },

    currentUser( state ) {
        return state._currentUser
    },

    signInEmail() {
        return window.localStorage.getItem( 'userEmail' )
    },

    userData() {
        const userData = window.localStorage.getItem( 'userData' )
        return userData ? JSON.parse( userData ) : null
    },

    userById( state ) {
        return ( id: string ) => _find( state._users, { id: id } )
    },

    userByUid( state ) {
        return ( uid: string ) => _find( state._users, { userUid: uid } )
    },

    userVotesByImageId( state ) {
        return ( imageId: string ) => {
            const associationCounter: { [associationId: string]: number } = {}

            _forEach( state._users, ( ( user: IUser ) => {
                if ( user.relatedVotes[imageId] ) {
                    user.relatedVotes[imageId].forEach( ( i: string ) => {
                        associationCounter[i] = ( associationCounter[i] ? associationCounter[i] : 0 ) + 1
                    } )
                }
            } ) )

            return associationCounter
        }
    },

    userHasVotedForImage( state ) {
        return ( associationId: string, imageId: string ) => {
            if ( state._currentUser.relatedVotes[imageId] ) {
                return state._currentUser.relatedVotes[imageId].indexOf( associationId ) !== -1
            } else {
                return false
            }
        }
    },

    userHasFirstVote( state, getters ) {
        return ( associationId: string, imageId: string ) => {
            return getters.userVotesByImageId( imageId )[associationId] === 1 && getters.userHasVotedForImage( associationId, imageId )
        }
    },

    totalVotesByUser( state, getters ) {
        return ( id: number ) => {
            let totalVotes = 0

            if ( getters.userById( id ) ) {
                _forEach( getters.userById( id )!.relatedVotes, ( vote: any ) => totalVotes += vote.length )
            }

            return totalVotes
        }
    },

    totalUsers( state ) {
        return Object.keys( state._users ).length
    }
} as GetterTree<State, any>

const mutations = {
    setCurrentUser( state, user: IUser ) {
        Vue.set( state, '_currentUser', user || defaultUser )
    },

    updateUserVotes( state, votes ) {
        Vue.set( state._currentUser, 'relatedVotes', votes )
    },

    updateUserAssociations( state, associations ) {
        Vue.set( state._currentUser, 'relatedAssociations', associations )
    },

    updateUserComments( state, comments ) {
        Vue.set( state._currentUser, 'relatedComments', comments )
    },

    setUserData( state, user: IUser ) {
        localStorage.setItem( 'userData', JSON.stringify( user ) )
    },

    setSignInLink( state, email: string ) {
        localStorage.setItem( 'userEmail', email )
    }
} as MutationTree<State>

const actions = {
    async requestSignInLink( { commit, state }, email: string ) {
        commit( 'setSignInLink', email )
        commit( 'setUserData', state._currentUser )
        authService.languageCode = i18n.locale

        try {
            await sendSignInLink( email )
        } catch( error ) {
            console.error( error )
        }
    },

    setupUserObserver( { state, commit, getters, dispatch } ) {
        authService.onAuthStateChanged( ( user ) => {
            const url = window.location.href

            if ( !authService.isSignInWithEmailLink( url ) ) return

            if ( user ) {
                const registeredUser = _find( state._users, { userUid: user.uid } )
                const localUser = getters.userData

                if ( !localUser && !state._currentUser.hasAccount && !registeredUser ) {
                    dispatch( 'set', {
                        name: _sample( starNames ),
                        hasAccount: true,
                        isRegistered: true,
                        userUid: user.uid,
                        preferredLanguage: i18n.locale,
                        relatedAssociations: [],
                        relatedComments: [],
                        relatedVotes: {}
                    } )
                    .then( async ( result ) => {
                        window.localStorage.removeItem( 'userData' )
                        commit( 'setCurrentUser', _find( state._users, { id: await result } ) )
                    } )
                } else if ( localUser && localUser.hasAccount && !registeredUser ) {
                    dispatch( 'patch', {
                        id: localUser.id,
                        name: _sample( starNames ),
                        isRegistered: true,
                        userUid: user.uid,
                        preferredLanguage: localUser.preferredLanguage,
                        relatedAssociations: localUser.relatedAssociations,
                        relatedComments: localUser.relatedComments,
                        relatedVotes: localUser.relatedVotes
                    } )
                    .then( async ( result ) => {
                        commit( 'setCurrentUser', _find( state._users, { id: await result } ) )
                    } )
                } else {
                    commit( 'setCurrentUser', registeredUser )
                }
            }
        } )
    },

    initUser( { state, commit, dispatch, getters } ) {
        const user = authService.currentUser

        if ( user ) {
            commit( 'setCurrentUser', _find( state._users, { userUid: user.uid } ) )
        } else {
            const id = getters['dbRef'].doc().id

            defaultUser.id = id

            commit( 'setCurrentUser', defaultUser )
            dispatch( 'setupUserObserver' )
        }
    },

    setRelatedComment( { commit, state, dispatch }, commentId: string ) {
        const usersComments = state._currentUser.relatedComments

        usersComments.push( commentId )

        if ( authService.currentUser ) {
            dispatch( 'patch', {
                id: state._currentUser.id,
                relatedComments: arrayUnion( commentId )
            } )
            .then( ( result ) => {
                commit( 'updateUserComments', usersComments )
            } )
        }
    },

    setRelatedAssociation( { state, dispatch, commit }, associationId: string ) {
        const usersAssociations = state._currentUser.relatedAssociations

        usersAssociations.push( associationId )

        if ( authService.currentUser ) {
            dispatch( 'patch', {
                id: state._currentUser.id,
                relatedAssociations: arrayUnion( associationId )
            } )
            .then( ( result ) => {
                commit( 'updateUserAssociations', usersAssociations )
            } )
        } else {
            commit( 'updateUserAssociations', usersAssociations )
        }
    },

    setRelatedVote( { commit, state, dispatch, rootState }, associationId: string ) {
        const imageId = rootState.imagesModule._currentImage.id
        const usersVotes = state._currentUser.relatedVotes
        const usersVotesForImage = usersVotes[imageId]
        const voteObject: { [ key: string ]: Array<string> } = {}

        if ( !state._currentUser.hasAccount ) {
            Vue.set( state._currentUser, 'hasAccount', true )

            dispatch( 'set', state._currentUser )
        }

        if ( usersVotesForImage && usersVotesForImage.indexOf( associationId ) === -1 ) {
            usersVotesForImage.push( associationId )
            usersVotes[imageId] = usersVotesForImage
        } else {
            usersVotes[imageId] = [associationId]
        }

        dispatch( 'patch', {
            id: state._currentUser.id,
            relatedVotes: usersVotes
        } )
        .then( ( result ) => {
            commit( 'updateUserVotes', usersVotes )
        } )
    },

    removeRelatedVote( { commit, state, dispatch, rootState }, associationId: string ) {
        const imageId = rootState.imagesModule._currentImage.id
        const usersVotes = state._users[state._currentUser.id].relatedVotes
        const usersVotesForImage = usersVotes[imageId]
        const associationIndex = usersVotesForImage.indexOf( associationId )

        if ( usersVotesForImage && associationIndex !== -1 ) {
            associationIndex === 0
                ? delete usersVotes[imageId]
                : usersVotes[imageId] = usersVotesForImage.splice( associationIndex, 1 )

            dispatch( 'patch', {
                id: state._currentUser.id,
                relatedVotes: usersVotes
            } )
            .then( ( result ) => {
                commit( 'updateUserVotes', usersVotes )
            } )
        }
    },

    logoutCurrentUser( { commit } ) {
        authService.signOut()
        commit( 'setCurrentUser', defaultUser )
    }
} as ActionTree<State, any>

const UsersModule = {
    firestorePath: 'Users',
    firestoreRefType: 'collection',
    moduleName: 'usersModule',
    statePropName: '_users',
    namespaced: true,
    state: new State(),
    mutations,
    getters,
    actions
}

export default UsersModule
